<template>
    <div>

        <Card :header="$t('priceDeviations')" overlay :loading="requestData.getLoading()">

            <ErrorMessage :value="requestData.getError()" />

            <template #nav>
                <div class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <Multiselect
                            :key="`campaigns-${spaceId}`"
                            :fetch-options-function="fetchCampaigns"
                            :placeholder="$t('selectCampaign')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="requestData.getLoading()"
                            show-checkboxes
                            show-selected
                            multiple
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="campaigns = $event"
                            @change="products.length && loadData()"
                        />
                        <Multiselect
                            v-show="showProductGroupsResults"
                            :key="`groups-${spaceId}`"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="requestData.getLoading()"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="groups = $event"
                            @change="products.length && showProductGroupsResults && loadData()"
                        />
                        <Multiselect
                            :key="`products-${spaceId}`"
                            :fetch-options-function="fetchProducerProducts"
                            :placeholder="$t('findProduct')"
                            preserve-search
                            asynchronous
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="requestData.getLoading()"
                            show-checkboxes
                            show-selected
                            multiple
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="products = $event"
                            @change="products.length && loadData()"
                        />
                        <FormSelectTree
                            v-show="!showProductGroupsResults"
                            :key="`categories-${spaceId}`"
                            ref="FormTreeSelect"
                            v-model="categories"
                            :disabled="requestData.getLoading()"
                            :load-options="fetchCategories"
                            :normalizer="normalizer"
                            :placeholder="$t('category')"
                            multiple
                            class="width-200"
                            show-count-value
                            @change="products.length && !showProductGroupsResults && loadData()"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="showProductGroupsResults" switch>{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

        </Card>

        <div class="d-flex flex-column">

            <DeviationsFromRecommendedPrice />

            <DeviationsFromRecommendedPriceInTime />

        </div>

    </div>
</template>



<script>
    import FormSelectTree from '@/components/common/FormSelectTree.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import DeviationsFromRecommendedPrice from './DeviationsFromRecommendedPrice.vue';
    import DeviationsFromRecommendedPriceInTime from './DeviationsFromRecommendedPriceInTime.vue';

    export default {
        name: 'PriceDeviations',
        components: {
            Multiselect,
            FormSelectTree,
            DeviationsFromRecommendedPrice,
            DeviationsFromRecommendedPriceInTime,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestData: this.$req('space/ProductsPriceChart'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestProductsTree: this.$req('space/ProductsTree'),
                requestSpaceGroups: this.$req('producerProductGroups/spaceGroups'),
                producerProduct: this.$req('space/ProducerProducts'),
                requestUpdatePrice: this.$req('producerProduct/product'),
                showProductGroupsResults: false,
                campaigns: [],
                groups: [],
                products: [],
                categories: [],
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = { startDate, endDate };
                if (this.products?.length) requestParams.products = this.products.join(',');
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = this.categories.join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = this.groups.join(',');
                return { urlParams, requestParams };
            },
            data() {
                return this.requestData.getData() || {};
            },
        },
        watch: {
            spaceId() {
                this.resetData();
                this.resetFilters();
                if (this.products.length) this.loadData();
            },
            timeRange() {
                if (this.products.length) this.loadData();
            },
            showProductGroupsResults() {
                if (this.products.length) this.loadData();
            },
        },
        methods: {
            resetData() {
                this.requestData.abortAll();
                this.requestData.resetAll();
            },
            resetFilters() {
                this.campaigns = [];
                this.groups = [];
                this.products = [];
                this.categories = [];
            },
            loadData() {
                this.requestData.get(this.apiParams);
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchProductsTree(categoryId) {
                return this.requestProductsTree.get({ urlParams: { spaceId: this.spaceId }, requestParams: { category: categoryId } });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            fetchProducerProducts({ search }) {
                return this.producerProduct.get({ urlParams: { spaceId: this.spaceId }, requestParams: { search } });
            },
            fetchCategories({ parentNode }) {
                return this.fetchProductsTree(parentNode?.entityId);
            },
            normalizer(node) {
                return {
                    id: node.entityId,
                    label: node.text,
                    children: node.children === true ? null : node.children,
                    isDisabled: node.type === 'product',
                };
            },
        },
    };
</script>
